import { gql } from '@apollo/client';

// Project Fragments
export const PROJECT_FRAGMENT = gql`
  fragment ProjectFields on Project {
    id
    name
    description
    followUp
    type
    status
    priority
    size
    estimate
    startDate
    dueDate
    region
    groupName
    inScope
    outOfScope
    expectedOutcomes
    keyFeaturesP0
    keyFeaturesP1
    keyFeaturesP2
    progress
    tasksCompleted
    tasksTotal
    createdAt
    updatedAt
    client {
      id
      name
      company
      status
    }
    developers {
      role
      overloadReason
      resource {
        id
        name
        email
        level
        load
      }
    }
    labels {
      id
      name
      color
    }
    phases {
      id
      name
      startDate
      dueDate
      createdAt
    }
    tasks {
      id
      name
      description
      status
      dueDate
      phaseId
      taskType
      externalManager
      assignedTo {
        id
        name
        email
        level
      }
      comments {
        id
        content
        createdBy
        createdAt
      }
    }
    notes {
      id
      title
      content
      createdAt
      updatedAt
    }
    assets {
      id
      name
      url
      fileType
      fileSize
      createdAt
    }
  }
`;

// Queries
export const GET_PROJECTS = gql`
  query GetProjects($status: String, $priority: String, $clientId: ID, $size: String, $type: String) {
    projects(status: $status, priority: $priority, clientId: $clientId, size: $size, type: $type) {
      ...ProjectFields
    }
  }
  ${PROJECT_FRAGMENT}
`;

export const GET_PROJECT = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      ...ProjectFields
    }
  }
  ${PROJECT_FRAGMENT}
`;

export const GET_ACTIVE_PROJECTS = gql`
  query GetActiveProjects {
    activeProjects {
      id
      name
      status
      followUp
    }
  }
`;

export const GET_CLIENTS = gql`
  query GetClients($status: String, $industry: String, $search: String, $page: Int, $limit: Int) {
    clients(status: $status, industry: $industry, search: $search, page: $page, limit: $limit) {
      clients {
        id
        name
        email
        company
        country
        industry
        status
        projectCount
        createdAt
      }
      total
      page
      totalPages
    }
  }
`;

export const GET_CLIENT = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      id
      name
      email
      company
      country
      industry
      status
      projectCount
    }
  }
`;

export const GET_RESOURCES = gql`
  query GetResources($skill: String, $level: String, $search: String, $page: Int, $limit: Int, $sortBy: String) {
    resources(skill: $skill, level: $level, search: $search, page: $page, limit: $limit, sortBy: $sortBy) {
      resources {
        id
        name
        email
        skill
        level
        load
        projectCount
        createdAt
        projects {
          id
          name
          status
          priority
          progress
          developers {
            resource {
              id
            }
            role
          }
        }
      }
      total
      page
      totalPages
    }
  }
`;

export const GET_RESOURCE = gql`
  query GetResource($id: ID!) {
    resource(id: $id) {
      id
      name
      email
      skill
      level
      load
      projectCount
    }
  }
`;

export const GET_CATALOGS = gql`
  query GetCatalogs($type: String) {
    catalogs(type: $type) {
      id
      type
      name
      order
      level
      color
      weightJunior
      weightMid
      weightSenior
    }
  }
`;

export const GET_LABELS = gql`
  query GetLabels {
    labels {
      id
      name
      color
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers {
    users {
      id
      name
      email
      role
      enabled
      department
      title
      avatar
      createdAt
    }
  }
`;

// Auth Mutations
export const LOGIN_USER = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        name
        email
        role
        avatar
      }
    }
  }
`;

export const REGISTER_USER = gql`
  mutation Register($name: String!, $email: String!, $password: String!) {
    register(name: $name, email: $email, password: $password) {
      token
      user {
        id
        name
        email
        role
        avatar
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
     deleteUser(id: $id)
  }
`;

export const CREATE_USER_ADMIN = gql`
  mutation CreateUser($name: String!, $email: String!, $password: String!, $role: String!) {
    createUser(name: $name, email: $email, password: $password, role: $role) {
      id
      name
      email
      role
      enabled
      createdAt
    }
  }
`;

export const ADMIN_UPDATE_USER = gql`
  mutation AdminUpdateUser($id: ID!, $name: String, $email: String, $role: String, $password: String, $enabled: Boolean) {
    adminUpdateUser(id: $id, name: $name, email: $email, role: $role, password: $password, enabled: $enabled) {
      id
      name
      email
      role
      enabled
      createdAt
    }
  }
`;

export const TOGGLE_USER_ENABLED = gql`
  mutation ToggleUserEnabled($id: ID!, $enabled: Boolean!) {
    toggleUserEnabled(id: $id, enabled: $enabled) {
      id
      name
      email
      role
      enabled
    }
  }
`;

// Mutations
export const CREATE_PROJECT = gql`
  mutation CreateProject($input: ProjectInput!) {
    createProject(input: $input) {
      ...ProjectFields
    }
  }
  ${PROJECT_FRAGMENT}
`;

export const UPDATE_PROJECT = gql`
  mutation UpdateProject($id: ID!, $input: ProjectInput!) {
    updateProject(id: $id, input: $input) {
      ...ProjectFields
    }
  }
  ${PROJECT_FRAGMENT}
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id)
  }
`;

export const DELETE_PROJECTS = gql`
  mutation DeleteProjects($ids: [ID!]!) {
    deleteProjects(ids: $ids)
  }
`;

export const UPDATE_PROJECT_DEVELOPERS = gql`
  mutation UpdateProjectDevelopers($projectId: ID!, $developers: [ProjectDeveloperInput!]!) {
    updateProjectDevelopers(projectId: $projectId, developers: $developers) {
      id
      developers {
        role
        overloadReason
        resource {
          id
          name
          email
          level
          load
        }
      }
    }
  }
`;

export const CREATE_CLIENT = gql`
  mutation CreateClient($input: ClientInput!) {
    createClient(input: $input) {
      id
      name
      email
      company
      country
      industry
      status
    }
  }
`;

export const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: ClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      name
      email
      company
      country
      industry
      status
    }
  }
`;

export const DELETE_CLIENT = gql`
  mutation DeleteClient($id: ID!) {
    deleteClient(id: $id)
  }
`;

export const DELETE_CLIENTS = gql`
  mutation DeleteClients($ids: [ID!]!) {
    deleteClients(ids: $ids)
  }
`;

export const CREATE_RESOURCE = gql`
  mutation CreateResource($input: ResourceInput!) {
    createResource(input: $input) {
      id
      name
      email
      skill
      level
      load
    }
  }
`;

export const UPDATE_RESOURCE = gql`
  mutation UpdateResource($id: ID!, $input: ResourceInput!) {
    updateResource(id: $id, input: $input) {
      id
      name
      email
      skill
      level
      load
    }
  }
`;

export const DELETE_RESOURCE = gql`
  mutation DeleteResource($id: ID!) {
    deleteResource(id: $id)
  }
`;

export const DELETE_RESOURCES = gql`
  mutation DeleteResources($ids: [ID!]!) {
    deleteResources(ids: $ids)
  }
`;

export const CREATE_CATALOG = gql`
  mutation CreateCatalog($input: CatalogInput!) {
    createCatalog(input: $input) {
      id
      type
      name
      order
      level
      color
      weightJunior
      weightMid
      weightSenior
    }
  }
`;

export const UPDATE_CATALOG = gql`
  mutation UpdateCatalog($id: ID!, $input: CatalogInput!) {
    updateCatalog(id: $id, input: $input) {
      id
      type
      name
      order
      level
      color
      weightJunior
      weightMid
      weightSenior
    }
  }
`;

export const DELETE_CATALOG = gql`
  mutation DeleteCatalog($id: ID!) {
    deleteCatalog(id: $id)
  }
`;

export const CREATE_LABEL = gql`
  mutation CreateLabel($input: LabelInput!) {
    createLabel(input: $input) {
      id
      name
      color
    }
  }
`;

export const UPDATE_LABEL = gql`
  mutation UpdateLabel($id: ID!, $input: LabelInput!) {
    updateLabel(id: $id, input: $input) {
      id
      name
      color
    }
  }
`;

export const DELETE_LABEL = gql`
  mutation DeleteLabel($id: ID!) {
    deleteLabel(id: $id)
  }
`;

export const ADD_TASK = gql`
  mutation AddTask($projectId: ID!, $input: ProjectTaskInput!) {
    addTask(projectId: $projectId, input: $input) {
      id
      progress
      tasksCompleted
      tasksTotal
      tasks {
        id
        name
        status
        dueDate
        phaseId
        taskType
        externalManager
        assignedTo {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask($projectId: ID!, $taskId: ID!, $input: ProjectTaskInput!) {
    updateTask(projectId: $projectId, taskId: $taskId, input: $input) {
      id
      progress
      tasksCompleted
      tasksTotal
      tasks {
        id
        name
        status
        dueDate
        phaseId
        taskType
        externalManager
        assignedTo {
          id
          name
        }
      }
    }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($projectId: ID!, $taskId: ID!) {
    deleteTask(projectId: $projectId, taskId: $taskId) {
      id
      progress
      tasksCompleted
      tasksTotal
      tasks {
        id
        name
        status
        dueDate
        phaseId
        taskType
        externalManager
        assignedTo {
          id
          name
        }
      }
    }
  }
`;

export const ADD_NOTE = gql`
  mutation AddNote($projectId: ID!, $input: ProjectNoteInput!) {
    addNote(projectId: $projectId, input: $input) {
      id
      notes {
        id
        title
        content
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_NOTE = gql`
  mutation UpdateNote($projectId: ID!, $noteId: ID!, $input: ProjectNoteInput!) {
    updateNote(projectId: $projectId, noteId: $noteId, input: $input) {
      id
      notes {
        id
        title
        content
        createdAt
        updatedAt
      }
    }
  }
`;

export const DELETE_NOTE = gql`
  mutation DeleteNote($projectId: ID!, $noteId: ID!) {
    deleteNote(projectId: $projectId, noteId: $noteId) {
      id
      notes {
        id
        title
        content
        createdAt
        updatedAt
      }
    }
  }
`;

export const ADD_ASSET = gql`
  mutation AddAsset($projectId: ID!, $input: ProjectAssetInput!) {
    addAsset(projectId: $projectId, input: $input) {
      id
      assets {
        id
        name
        url
        fileType
        fileSize
        createdAt
      }
    }
  }
`;

export const DELETE_ASSET = gql`
  mutation DeleteAsset($projectId: ID!, $assetId: ID!) {
    deleteAsset(projectId: $projectId, assetId: $assetId) {
      id
      assets {
        id
        name
        url
        fileType
        fileSize
        createdAt
      }
    }
  }
`;

export const ADD_TASK_COMMENT = gql`
  mutation AddTaskComment($projectId: ID!, $taskId: ID!, $content: String!) {
    addTaskComment(projectId: $projectId, taskId: $taskId, content: $content) {
      id
      tasks {
        id
        comments {
          id
          content
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const DELETE_TASK_COMMENT = gql`
  mutation DeleteTaskComment($projectId: ID!, $taskId: ID!, $commentId: ID!) {
    deleteTaskComment(projectId: $projectId, taskId: $taskId, commentId: $commentId) {
      id
      tasks {
        id
        comments {
          id
          content
          createdBy
          createdAt
        }
      }
    }
  }
`;

export const BULK_CREATE_CLIENTS = gql`
  mutation BulkCreateClients($input: [ClientInput!]!) {
    bulkCreateClients(input: $input) {
      id
      name
      email
      company
    }
  }
`;

export const BULK_CREATE_RESOURCES = gql`
  mutation BulkCreateResources($input: [ResourceInput!]!) {
    bulkCreateResources(input: $input) {
      id
      name
      email
    }
  }
`;

export const BULK_CREATE_PROJECTS = gql`
  mutation BulkCreateProjects($input: [ProjectBulkInput!]!) {
    bulkCreateProjects(input: $input) {
      id
      name
    }
  }
`;

export const ADD_PHASE = gql`
  mutation AddPhase($projectId: ID!, $input: ProjectPhaseInput!) {
    addPhase(projectId: $projectId, input: $input) {
      id
      phases {
        id
        name
        startDate
        dueDate
        createdAt
      }
    }
  }
`;

export const UPDATE_PHASE = gql`
  mutation UpdatePhase($projectId: ID!, $phaseId: ID!, $input: ProjectPhaseInput!) {
    updatePhase(projectId: $projectId, phaseId: $phaseId, input: $input) {
      id
      phases {
        id
        name
        startDate
        dueDate
        createdAt
      }
    }
  }
`;

export const DELETE_PHASE = gql`
  mutation DeletePhase($projectId: ID!, $phaseId: ID!) {
    deletePhase(projectId: $projectId, phaseId: $phaseId) {
      id
      phases {
        id
        name
        startDate
        dueDate
        createdAt
      }
      tasks {
        id
        name
        status
        dueDate
        phaseId
        taskType
        externalManager
        assignedTo {
          id
          name
        }
      }
    }
  }
`;

export const EXPORT_DATA = gql`
  query ExportData {
    exportData
  }
`;

export const IMPORT_DATA = gql`
  mutation ImportData($jsonData: String!) {
    importData(jsonData: $jsonData)
  }
`;

export const REORDER_TASKS = gql`
  mutation ReorderTasks($projectId: ID!, $taskIdsOrder: [ID!]!) {
    reorderTasks(projectId: $projectId, taskIdsOrder: $taskIdsOrder) {
      id
      progress
      tasksCompleted
      tasksTotal
      tasks {
        id
        name
        status
        dueDate
        phaseId
        taskType
        externalManager
        assignedTo {
          id
          name
        }
      }
    }
  }
`;


